import React, { Fragment } from 'react';
import styled from 'styled-components';
import { find } from 'ramda';
import UploadField from '~~elements/UploadField';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import Button from '~~elements/Button';
import { PlayerResource } from '~~apis/resource';
import TTMultilingual from './Multilingual';
import { playerPositionOptions, playerHandOptions } from '../Const';
import { useTournamentState } from '../useTournament';
import moment from 'moment';

const CurrentAvatar = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: url(${({ url }) => url});
  background-size: cover;
  background-position: center;
`;

const PlayerEdit = ({ playerState, playerDispatch, players, seasonUniqid, seasonTeamUniqid, done }) => {
  const { organizerUniqid } = useTournamentState();

  const handleInputOnchange = (e) => {
    playerDispatch({ type: 'updateColumn', payload: { [e.target.name]: e.target.value } });
  };

  const handleAvatarUpdate = (data) => {
    playerDispatch({ type: 'updateColumn', payload: { avatar_url: data.url, avatar_path: data.path } });
  };

  const handleRemoveAvatar = () => {
    playerDispatch({ type: 'removeAvatar' });
  };

  const handleUpdate = () => {
    const playerName = playerState.newName.trim();
    const playerNumber = playerState.newNumber.trim();
    if (!playerName || !playerNumber) {
      return playerDispatch({ type: 'error', payload: TTMultilingual(`ERROR.NAME_NUMBER_REQUIRED`) });
    }

    const player = find((p) => p.number === playerNumber, players);
    if (player && playerNumber !== playerState.number) {
      return playerDispatch({ type: 'error', payload: TTMultilingual(`ERROR.NUMBER_DUPLICATE`) });
    }

    const birthday = new Date(playerState.birthday);
    let birthdayValue = '';
    if (playerState.birthday && moment(birthday).isValid()) {
      birthdayValue = moment(birthday).format('YYYY/MM/DD');
    }

    playerDispatch({ type: 'playerStatus', payload: 'PROCESSING' });
    PlayerResource.updatePlayerInSeasonTeamByOrganizer({
      leagueUniqid: organizerUniqid,
      seasonUniqid,
      seasonTeamUniqid,
      playerNumber: playerState.number,
      data: { ...playerState, name: playerName, birthday: birthdayValue, number: playerNumber },
    })
      .then(() => {
        playerDispatch({ type: 'playerStatus', payload: 'FINISHED' });
        setTimeout(() => done(), 500);
      })
      .catch(() => {
        playerDispatch({ type: 'error', payload: TTMultilingual(`COMMON.UNKNOWN_ERROR`) });
      });
  };

  const handleCancel = () => {
    playerDispatch({ type: 'reset' });
  };

  return (
    <Fragment>
      {playerState.avatar_url ? (
        <div className="registration--item-avatar">
          <CurrentAvatar url={playerState.avatar_url}></CurrentAvatar>
          <UploadField
            styleType="box"
            url="/api/media"
            customBtn={(onClick) => (
              <Button width="unset" color="white" onClick={onClick}>
                {TTMultilingual(`FORM.REPLACE`)}
              </Button>
            )}
            onChange={handleAvatarUpdate}
          />
          <Button width="unset" color="red" onClick={handleRemoveAvatar} mini>
            {TTMultilingual(`COMMON.REMOVE`)}
          </Button>
        </div>
      ) : (
        <div className="registration--item-avatar default">
          <UploadField
            styleType="box"
            url="/api/media"
            customBtn={(onClick) => (
              <Button width="unset" color="white" onClick={onClick}>
                {TTMultilingual(`FORM.UPLOAD`)}
              </Button>
            )}
            onChange={handleAvatarUpdate}
          />
        </div>
      )}
      <div>
        {TTMultilingual(`COLUMN.NAME`)}：
        <Input
          width="100%"
          name="newName"
          onChange={handleInputOnchange}
          value={playerState.newName}
          {...(!playerState.player_cross_league
            ? {}
            : { disabled: true, title: TTMultilingual(`CROSS_LEAGUE_DENY_WORDING`) })}
        />
      </div>
      <div>
        {TTMultilingual(`COLUMN.NUMBER`)}：
        <Input width="100%" name="newNumber" onChange={handleInputOnchange} value={playerState.newNumber} />
      </div>
      <div>
        {TTMultilingual(`COLUMN.BIRTHDAY`)}：
        <Input
          width="100%"
          name="birthday"
          type="date"
          onChange={handleInputOnchange}
          value={playerState.birthday}
          {...(!playerState.player_cross_league
            ? {}
            : { disabled: true, title: TTMultilingual(`CROSS_LEAGUE_DENY_WORDING`) })}
        />
      </div>
      <div>
        {TTMultilingual(`COLUMN.FIRST_POSITION`)}：
        <Select width="100%" name="first_position" onChange={handleInputOnchange} value={playerState.first_position}>
          {playerPositionOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`POSITION.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        {TTMultilingual(`COLUMN.SECOND_POSITION`)}：
        <Select width="100%" name="second_position" onChange={handleInputOnchange} value={playerState.second_position}>
          {playerPositionOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`POSITION.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        {TTMultilingual(`COLUMN.P_HAND`)}：
        <Select width="100%" name="p_hand" onChange={handleInputOnchange} value={playerState.p_hand}>
          {playerHandOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`HAND.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        {TTMultilingual(`COLUMN.B_HAND`)}：
        <Select width="100%" name="b_hand" onChange={handleInputOnchange} value={playerState.b_hand}>
          {playerHandOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`HAND.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      {playerState.playerStatus === 'ERROR' && playerState.errorMsg && (
        <div className="registration--error-msg">{playerState.errorMsg}</div>
      )}
      {playerState.playerStatus === 'FINISHED' && (
        <div className="registration--success-msg">{TTMultilingual(`SUCCESS.UPDATE`)}</div>
      )}
      {playerState.playerStatus === 'PROCESSING' && (
        <div className="registration--discribe-msg">{TTMultilingual(`COMMON.PROCESSING`)}</div>
      )}
      <div className="registration--item-control">
        <Button
          width="unset"
          nowrap
          color="blue"
          mini
          onClick={handleUpdate}
          disabled={playerState.playerStatus === 'PROCESSING'}
        >
          {TTMultilingual(`COMMON.UPDATE`)}
        </Button>
        <Button width="unset" nowrap color="red" mini onClick={handleCancel}>
          {TTMultilingual(`COMMON.CANCEL`)}
        </Button>
      </div>
    </Fragment>
  );
};

export default PlayerEdit;
